import React, { useState } from "react";
import { contractRepo, installmentRepo, ORDER_TYPE } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { useMockCustomer } from "./useMockCustomer";
import PaymentQrModal from "../../components/PaymentQrModal";
import { Button, Card, EmptyState, Modal, SignaturePad, StatusBadge, Tabs, baht, formatDate, useAsync } from "../../components/ui";

export default function CustomerContracts() {
  const { orgId, toast } = useApp();
  const { tx } = useI18n();
  const { customer } = useMockCustomer(orgId);
  const [tab, setTab] = useState("contracts");
  const [payPeriod, setPayPeriod] = useState(null);
  const [activePlan, setActivePlan] = useState(null);
  const [signing, setSigning] = useState(null);
  const [signature, setSignature] = useState(null);
  const [saving, setSaving] = useState(false);

  const { data: contracts, reload: reloadContracts } = useAsync(() => (customer ? contractRepo.all(orgId, (row) => row.customerId === customer.id && row.status !== "draft") : Promise.resolve([])), [orgId, customer], []);
  const { data: plans, reload } = useAsync(() => (customer ? installmentRepo.all(orgId, (row) => row.customerId === customer.id) : Promise.resolve([])), [orgId, customer], []);

  const markPaid = async () => {
    if (!activePlan || !payPeriod) return;
    const schedule = activePlan.schedule.map((row) => (row.period === payPeriod.period ? { ...row, status: "paid", paidAt: new Date().toISOString() } : row));
    const outstanding = schedule.filter((s) => s.status !== "paid").reduce((sum, s) => sum + s.amount, 0);
    await installmentRepo.update(orgId, activePlan.id, { schedule, outstanding });
    setPayPeriod(null);
    setActivePlan(null);
    reload();
    toast(tx("ชำระค่างวดสำเร็จ", "Installment paid"), "success");
  };

  const confirmSign = async () => {
    if (!signing || !customer) return;
    if (!signature) return toast(tx("กรุณาเซ็นชื่อก่อนยืนยัน", "Please sign before confirming"), "error");
    setSaving(true);
    try {
      // ตรวจสถานะล่าสุดก่อน กันเซ็นซ้ำ/เซ็นสัญญาที่ร้านถอนกลับไปเป็นร่างแล้ว
      const fresh = await contractRepo.get(orgId, signing.id);
      if (!fresh || fresh.customerId !== customer.id || fresh.status !== "waiting_sign") {
        throw new Error(tx("สัญญานี้ไม่อยู่ในสถานะรอลงนามแล้ว", "This contract is no longer awaiting signature"));
      }
      await contractRepo.update(orgId, signing.id, { status: "active", signedAt: new Date().toISOString(), signedBy: customer.name, signature });
      setSigning(null);
      setSignature(null);
      reloadContracts();
      toast(tx("ลงนามสัญญาเรียบร้อย", "Contract signed"), "success");
    } catch (error) {
      toast(error?.message || tx("ลงนามไม่สำเร็จ", "Signing failed"), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Tabs tabs={[{ key: "contracts", label: tx("สัญญา", "Contracts") }, { key: "installments", label: tx("ค่างวด", "Installments") }]} value={tab} onChange={setTab} />

      {tab === "contracts" &&
        (!contracts?.length ? (
          <EmptyState text={tx("ยังไม่มีสัญญา", "No contracts")} />
        ) : (
          contracts.map((row) => (
            <Card key={row.id}>
              <div className="u-between">
                <div>
                  <div className="u-strong">{row.title}</div>
                  <div className="u-fs-13 u-muted">{row.docNo} · {baht(row.value, 0)}</div>
                </div>
                <StatusBadge status={row.status} />
              </div>
              {row.status === "waiting_sign" ? (
                <Button variant="primary" block className="u-mt-16" onClick={() => { setSignature(null); setSigning(row); }}>{tx("อ่านและลงนาม", "Review & sign")}</Button>
              ) : null}
            </Card>
          ))
        ))}

      {tab === "installments" &&
        (!plans?.length ? (
          <EmptyState text={tx("ไม่มีสัญญาผ่อนชำระ", "No installment plans")} />
        ) : (
          plans.map((plan) => (
            <Card key={plan.id} title={plan.contractNo} subtitle={tx(`ค้างชำระ ${baht(plan.outstanding)}`, `Outstanding ${baht(plan.outstanding)}`)}>
              {plan.schedule.filter((row) => row.status !== "paid").slice(0, 2).map((row) => (
                <div key={row.period} className="u-between" style={{ padding: "6px 0" }}>
                  <span className="u-fs-13">{tx("งวดที่", "Period")} {row.period} · {formatDate(row.dueAt)}</span>
                  <Button size="sm" variant="primary" onClick={() => { setActivePlan(plan); setPayPeriod(row); }}>{baht(row.amount, 0)}</Button>
                </div>
              ))}
            </Card>
          ))
        ))}

      <Modal
        open={!!signing}
        title={signing?.docNo}
        onClose={() => { setSigning(null); setSignature(null); }}
        footer={<><Button onClick={() => { setSigning(null); setSignature(null); }}>{tx("ยกเลิก", "Cancel")}</Button><Button variant="primary" disabled={saving} onClick={confirmSign}>{tx("ยืนยันการลงนาม", "Confirm signature")}</Button></>}
      >
        {signing ? (
          <>
            <div className="u-strong">{signing.title}</div>
            <div className="u-fs-13 u-muted u-mb-8">{baht(signing.value, 0)} · {formatDate(signing.startAt)} - {formatDate(signing.endAt)}</div>
            <ol className="u-fs-13" style={{ paddingLeft: 18 }}>
              {(signing.terms || []).map((term, index) => (
                <li key={index} style={{ marginBottom: 6 }}>{term}</li>
              ))}
            </ol>
            <SignaturePad onChange={setSignature} />
          </>
        ) : null}
      </Modal>

      <PaymentQrModal
        open={!!payPeriod}
        title={tx(`ชำระงวดที่ ${payPeriod?.period}`, `Pay period ${payPeriod?.period}`)}
        amount={payPeriod?.amount || 0}
        orderType={ORDER_TYPE.installment}
        reference={activePlan?.contractNo}
        items={[{ productName: `${activePlan?.contractNo}`, quantity: 1, price: payPeriod?.amount || 0 }]}
        onClose={() => setPayPeriod(null)}
        onPaid={markPaid}
      />
    </>
  );
}
